<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view :key="$route.fullPath" />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import { tenant } from '@/constants/config'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return 'horizontal'
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
  mounted() {
    this.initAppTitle()
  },
  methods: {
    initAppTitle() {
      // eslint-disable-next-line no-nested-ternary
      const title = `${tenant.toLowerCase() === 'jhatta' ? 'Jhatta No 1 Cash Delivery Service' : (tenant.includes('mng') ? 'MNG' : (tenant.charAt(0).toUpperCase() + tenant.slice(1)))} | Tracking`
      document.title = title
    },
  },
}
</script>
<style>
/* 1. Full-height flex chain on root containers */
html,
body,
#app,
.horizontal-layout,
.vertical-layout {
  height: 100% !important;
  min-height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
  margin: 0 !important;
  padding: 0 !important;
}

body {
  background-color: #ffffff !important;
}

/* 2. Top Navbar: fixed to its natural height at top */
.header-navbar,
.header-navbar.navbar-horizontal,
.header-navbar.navbar-fixed,
.header-navbar.floating-nav {
  position: relative !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  z-index: 10 !important;
  flex-shrink: 0 !important;
}

.header-navbar-shadow {
  display: none !important;
}

/* 3. Middle Content: stretches to absorb 100% of the remaining space */
.horizontal-layout .app-content,
.app-content.content,
.content-wrapper,
.content-body {
  padding: 0 !important;
  margin: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 auto !important;
  min-height: 0 !important;
}

/* 4. Footer: fixed to its natural height at bottom with zero extra padding */
footer.footer,
.footer.footer-static,
.footer.footer-fixed,
.footer-wrapper {
  position: relative !important;
  margin: 0 !important;
  padding: 0 !important;
  min-height: unset !important;
  background-color: #ffffff !important;
  border-radius: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
  z-index: 10 !important;
  flex-shrink: 0 !important;
}

@media (max-width: 576px) {
  .horizontal-layout .app-content,
  .app-content.content,
  .content-wrapper,
  .content-body {
    min-height: unset !important;
  }
}
</style>
